/* Ink simulation: pigment particles sprayed in from a card edge, churned by a
 * curl-noise flow field, settling like dye in water. Pure physics + canvas
 * painting — the component/loop/DOM side lives in InkCanvas.tsx. */

import { makeNoise, spriteOf } from "./ink-color.js";

/** A pulse tells the canvas which edge the changed control lives on (left =
 *  topic rail, top = priority rail, br = custom swatches), where along that
 *  edge, and the new color. `point` is a radial burst from an arbitrary
 *  (ox,oy) ∈ [0,1] — the mobile sheet's topic-drop flood that blooms outward
 *  from where the color blob struck. */
export interface InkPulse {
  id: number;
  src: "left" | "top" | "br" | "point";
  ox: number;
  oy: number;
  col: string;
  /** Fraction of the usual spray (subtopics fire a small puff). */
  amount?: number;
  /** Override of the jet's spread fraction. */
  spread?: number;
}

/** Opt-in diagnostics: `localStorage.inkDebug = "1"` traces emit → spawn →
 *  frame stamping, so a "no bloom" report shows which stage went dark. */
export function inkDebug(): boolean {
  try { return localStorage.getItem("inkDebug") === "1"; } catch { return false; }
}

/* Feel parameters baked from the design's chosen tweaks. */
export const SETTLE = 3500;
const CFG = {
  density: 65, turbulence: 100, swirl: 100, diffusion: 55, viscosity: 10,
  jet: 10, width: 100, softness: 45, strength: 25, retract: 0, cornerBias: 60,
};

/** Canvas-space rect of an opaque control the pigment must flow around. */
export interface Rect { x: number; y: number; w: number; h: number }

interface Particle {
  x: number; y: number; vx: number; vy: number; r: number;
  age: number; life: number; col: string; seed: number; spin: number;
  pullx: number; pully: number; hx: number; hy: number;
}

export interface InkState {
  parts: Particle[]; raf: number; last: number;
  W: number; H: number; dpr: number;
  lo: HTMLCanvasElement | null; lctx: CanvasRenderingContext2D | null;
  obstacles: Rect[];
  /** Blurred punch-out of the obstacle rects (lo-res space) — pigment renders
   *  as if flowing UNDER the controls, with a soft bleed at their edges. */
  mask: HTMLCanvasElement | null;
  /** Rect signature of the current mask — skips redundant rebakes. */
  maskSig?: string;
  /** Static dither pattern composited source-atop to break alpha banding. */
  grainPat?: CanvasPattern | null;
  /** Last debug-trace timestamp (inkDebug only). */
  logAt?: number;
}

export function newInkState(): InkState {
  return { parts: [], raf: 0, last: 0, W: 1, H: 1, dpr: 1, lo: null, lctx: null, obstacles: [], mask: null };
}

/** (Re)bake the obstacle mask. The fill is inset by the blur radius so the
 *  soft rim lies entirely INSIDE the control: pigment bleeds a few px over its
 *  edge, then is fully erased — and no clear "glow" halo is punched into the
 *  pigment OUTSIDE the control. Call after obstacles/size change. */
export function buildMask(s: InkState): void {
  if (!s.lo || s.obstacles.length === 0) { s.mask = null; s.maskSig = ""; return; }
  const sig = `${s.lo.width}x${s.lo.height}|` +
    s.obstacles.map((o) => `${o.x | 0},${o.y | 0},${o.w | 0},${o.h | 0}`).join(";");
  if (sig === s.maskSig && s.mask) return;
  s.maskSig = sig;
  let m = s.mask;
  if (!m || m.width !== s.lo.width || m.height !== s.lo.height) {
    m = document.createElement("canvas");
    m.width = s.lo.width;
    m.height = s.lo.height;
  }
  const k = m.width / s.W;
  const g = m.getContext("2d")!;
  g.clearRect(0, 0, m.width, m.height);
  const b = 1; // lo-res px: blur radius AND inset (≈3px CSS transition band)
  g.filter = `blur(${b}px)`;
  g.fillStyle = "#000";
  for (const ob of s.obstacles) {
    const w = ob.w * k - 2 * b, h = ob.h * k - 2 * b;
    if (w < 1 || h < 1) continue;
    g.fillRect(ob.x * k + b, ob.y * k + b, w, h);
  }
  s.mask = m;
}

// Budget: ~8 full streams may overlap. The NEWEST gesture always gets its full
// spray; past MAX the OLDEST particles get their remaining life capped so they
// FADE out (~0.5s) instead of vanishing mid-air, and a hard cap bounds the
// worst-case frame cost under click spam. Do not re-clamp the new spray to the
// remaining budget — that starved pulses after a few quick switches.
const MAX = 1440;
const HARD = 1900;

export function spawn(s: InkState, p: InkPulse): void {
  const { W, H } = s;
  let n = Math.round(30 + (CFG.density / 100) * 230);
  if (p.amount != null) n = Math.max(6, Math.round(n * p.amount));
  const r0k = 0.7 + (CFG.diffusion / 100) * 0.7;
  const spreadF = p.spread ?? 0.03 + (CFG.width / 100) * 0.3;

  const emit = (
    ox: number, oy: number, dx: number, dy: number, ax: number, ay: number,
    reach: number, spread: number, pullx: number, pully: number, hx: number, hy: number,
  ) => {
    const t = Math.random() * 2 - 1;
    const g = Math.exp(-t * t * 1.4); // bunch toward the jet's center
    const baseV = (reach / (SETTLE / 1000)) * (0.3 + (CFG.jet / 100) * 1.6);
    const px = ox + ax * t * spread + dx * Math.random() * 12;
    const py = oy + ay * t * spread + dy * Math.random() * 12;
    const sp = baseV * (0.5 + Math.random() * 1.05) * (0.55 + 0.45 * g);
    const perp = (Math.random() * 2 - 1) * baseV * 0.4;
    s.parts.push({
      x: px, y: py, vx: dx * sp + ax * perp, vy: dy * sp + ay * perp,
      r: (8 + Math.random() * 15) * r0k,
      age: 0, life: SETTLE * (0.78 + Math.random() * 0.5),
      col: p.col, seed: Math.random() * 1000, spin: Math.random() * 2 - 1,
      pullx, pully, hx, hy,
    });
  };

  if (p.src === "left") {
    const oy = p.oy * H, spread = H * spreadF;
    for (let i = 0; i < n; i++) emit(0, oy, 1, 0, 0, 1, W, spread, 1, 0, 0.06 * W, 0);
  } else if (p.src === "top") {
    const ox = p.ox * W, spread = W * spreadF;
    for (let i = 0; i < n; i++) emit(ox, 0, 0, 1, 1, 0, H, spread, 0, 1, 0, 0.06 * H);
  } else if (p.src === "point") {
    // Radial burst from (ox,oy): a dye drop blooming outward in every
    // direction. The curl field then churns it like the edge jets do.
    const ox = p.ox * W, oy = p.oy * H;
    const reach = Math.max(W, H) * 0.7;
    const baseV = (reach / (SETTLE / 1000)) * (0.3 + (CFG.jet / 100) * 1.6);
    for (let i = 0; i < n; i++) {
      const ang = Math.random() * Math.PI * 2;
      const dx = Math.cos(ang), dy = Math.sin(ang);
      const sp = baseV * (0.35 + Math.random() * 1.1);
      const jr = Math.random() * 8; // tiny origin jitter so it isn't a hard dot
      s.parts.push({
        x: ox + dx * jr, y: oy + dy * jr,
        vx: dx * sp, vy: dy * sp,
        r: (8 + Math.random() * 15) * r0k,
        age: 0, life: SETTLE * (0.78 + Math.random() * 0.5),
        col: p.col, seed: Math.random() * 1000, spin: Math.random() * 2 - 1,
        pullx: 0, pully: 0, hx: 0, hy: 0,
      });
    }
  } else {
    // "br" — custom: right half of the bottom edge + bottom half of the right
    // edge, densest right at the corner (corner-bias 60).
    const bottomSpread = W * 0.25, rightSpread = H * 0.25;
    const kBias = (CFG.cornerBias / 100) * 4.5;
    const toCorner = () => Math.pow(Math.random(), 1 / (1 + kBias));
    for (let i = 0; i < n; i++) {
      if (i % 2 === 0) {
        const ox = W * (0.5 + 0.5 * toCorner());
        emit(ox, H, -0.18, -0.98, 1, 0, H, bottomSpread, 0, 1, 0, 0.94 * H);
      } else {
        const oy = H * (0.5 + 0.5 * toCorner());
        emit(W, oy, -0.98, -0.18, 0, 1, W, rightSpread, 1, 0, 0.94 * W, 0);
      }
    }
  }
  const over = s.parts.length - MAX;
  for (let i = 0; i < over; i++) {
    const pt = s.parts[i]!;
    pt.life = Math.min(pt.life, pt.age + 500);
  }
  if (s.parts.length > HARD) s.parts.splice(0, s.parts.length - HARD);
}

export function step(s: InkState, dt: number, time: number): void {
  const curlAmt = (CFG.turbulence / 100) * 130;
  const swirl = (CFG.swirl / 100) * 2.2;
  const dragBase = 0.7 - (CFG.viscosity / 100) * 0.66;
  const growAmt = 10 + (CFG.diffusion / 100) * 80;
  const pullAmt = (CFG.retract / 100) * 7.5;
  const next: Particle[] = [];
  for (const pt of s.parts) {
    pt.age += dt * 1000;
    const e = Math.max(0, 1 - pt.age / pt.life); // remaining energy 1→0
    let fx = Math.sin(pt.y * 0.013 + time * 0.9 + pt.seed)
      + 0.5 * Math.sin(pt.y * 0.031 - time * 0.6 + pt.seed * 0.3);
    let fy = Math.cos(pt.x * 0.013 + time * 0.8 + pt.seed * 0.7)
      + 0.5 * Math.cos(pt.x * 0.029 + time * 0.5);
    fx += -pt.vy * 0.0016 * swirl * pt.spin;
    fy += pt.vx * 0.0016 * swirl * pt.spin;
    const curl = curlAmt * (0.28 + e);
    pt.vx += fx * curl * dt;
    pt.vy += fy * curl * dt;
    const pull = pullAmt * Math.pow(1 - e, 1.4);
    if (pt.pullx) pt.vx += (pt.hx - pt.x) * pull * dt;
    if (pt.pully) pt.vy += (pt.hy - pt.y) * pull * dt;
    const drag = Math.pow(dragBase, dt);
    pt.vx *= drag;
    pt.vy *= drag;
    pt.x += pt.vx * dt;
    pt.y += pt.vy * dt;
    pt.r += dt * growAmt * (0.4 + e);
    if (pt.age < pt.life) next.push(pt);
  }
  s.parts = next;
}

export function render(s: InkState, canvas: HTMLCanvasElement | null, now = 0): void {
  const { lo, lctx } = s;
  if (!lo || !lctx || !canvas) return;
  const sx = lo.width / s.W, sy = lo.height / s.H, rk = (sx + sy) / 2;
  const peakA = 0.05 + (CFG.strength / 100) * 0.42;
  lctx.clearRect(0, 0, lo.width, lo.height);
  lctx.globalCompositeOperation = "source-over";
  let stamped = 0;
  for (const pt of s.parts) {
    const e = Math.max(0, 1 - pt.age / pt.life);
    const fadeIn = Math.min(1, pt.age / 120);
    const a = peakA * fadeIn * (0.16 + 0.84 * e);
    if (a <= 0.003) continue;
    const x = pt.x * sx, y = pt.y * sy, r = Math.max(1, pt.r * rk);
    lctx.globalAlpha = a;
    lctx.drawImage(spriteOf(pt.col), x - r, y - r, r * 2, r * 2);
    stamped++;
  }
  lctx.globalAlpha = 1;
  if (s.mask) {
    // Pigment passes UNDER the controls: punch their (blurred, slightly inset)
    // rects out — the soft rim left by the blur is the edge bleed.
    lctx.globalCompositeOperation = "destination-out";
    lctx.drawImage(s.mask, 0, 0);
    lctx.globalCompositeOperation = "source-over";
  }
  if (now - (s.logAt ?? 0) > 500 && inkDebug()) {
    s.logAt = now;
    console.info(`[ink] frame parts=${s.parts.length} stamped=${stamped} ${s.W}x${s.H}@${s.dpr}`);
  }
  const ctx = canvas.getContext("2d")!;
  ctx.setTransform(s.dpr, 0, 0, s.dpr, 0, 0);
  ctx.clearRect(0, 0, s.W, s.H);
  ctx.imageSmoothingEnabled = true;
  const blur = 1 + (CFG.softness / 100) * 6;
  ctx.filter = `blur(${blur.toFixed(1)}px)`;
  ctx.drawImage(lo, 0, 0, s.W, s.H);
  ctx.filter = "none";
  // Dither: the blurred 8-bit gradients band visibly as particles fade. A
  // static noise pass masked to the pigment (source-atop) breaks the bands;
  // at this alpha it reads as paper grain, not shimmer.
  if (s.grainPat === undefined) s.grainPat = ctx.createPattern(makeNoise(), "repeat");
  if (s.grainPat) {
    ctx.globalCompositeOperation = "source-atop";
    ctx.globalAlpha = 0.05;
    ctx.fillStyle = s.grainPat;
    ctx.fillRect(0, 0, s.W, s.H);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = "source-over";
  }
}

export function clear(s: InkState, canvas: HTMLCanvasElement | null): void {
  const ctx = canvas?.getContext("2d");
  if (!ctx) return;
  ctx.setTransform(s.dpr, 0, 0, s.dpr, 0, 0);
  ctx.clearRect(0, 0, s.W, s.H);
}
